import { Link, router } from '@inertiajs/react'
import { Plus } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Button } from '@/components/ui/button'
import { EntitySummary } from '@/types'
import { formatCents } from '@/lib/utils'

type Props = {
  entities: EntitySummary[]
  search: string
  pagination: PaginationMeta
}

export default function EntitiesIndex({ entities, search, pagination }: Props) {
  return (
    <BackofficeLayout title="Entities" breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Entities' }]}>
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight">Entity Catalog</h1>
        <p className="text-sm text-muted-foreground">{pagination.total} {pagination.total === 1 ? 'entity type' : 'entity types'}</p>
        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" variant="outline" asChild className="gap-1.5 text-[13px] h-8">
            <Link href="/backoffice/entities/new">
              <Plus className="w-3.5 h-3.5" />
              New Entity
            </Link>
          </Button>
        </div>
      </div>

      <SearchInput value={search} placeholder="Search entities..." />

      <div className="mt-4">
        {entities.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <h3 className="text-sm font-medium text-foreground mb-1">No entity types configured yet</h3>
          </div>
        ) : (
          <div className="border rounded-lg">
            {entities.map((e, i) => (
              <div
                key={e.id}
                className={`px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                onClick={() => router.visit(`/backoffice/entities/${e.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <Link href={`/backoffice/entities/${e.id}`} className="text-sm font-medium hover:underline">{e.name}</Link>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {e.jurisdiction_label || e.jurisdiction_code || '—'} · {formatCents(e.state_fee_cents)} + {formatCents(e.service_fee_cents)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  )
}
